import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import { useActions, useLive, useNow, useRuntime, useSharedRemote } from './context';
import { useHasModule, useRemote } from './useRemote';
import type { Remote } from './useRemote';
import {
  addDays,
  formatDayLong,
  formatFreshness,
  formatMoney,
  formatStaleness,
  isoDayIn,
} from '../mirror/time';
import { StayCancelled, StayDay, StayOffers, StaySync } from '../wire';
import type {
  CalendarSync,
  ExternalCollision,
  Stay,
  StayCancelled as StayCancelledType,
  StaySync as StaySyncType,
  WireIdentity,
} from '../wire';
import { useLocale, useT } from '../messages';
import type { Messages, T, UiLocale } from '../messages';

/**
 * **Front desk** — lodging (spec §4.7), seven in the morning.
 *
 * Three lists rather than one, because `stayDay` answers in three and the
 * argument for that is the desk's: a key to cut, a folio to close, and a room
 * housekeeping must not turn are three different jobs, and a screen that made
 * the user filter one list would be making them do the classification
 * themselves.
 *
 * **Online**, for the reasons in `useRemote.ts`, and one that is particular to
 * lodging: the arrivals list is what the desk hands a key from, and a cached one
 * would hand a key to a party who cancelled at six.
 *
 * ## The fee is the server's, and this screen is where that pinches
 *
 * §4.7: *"a per-item policy of tiers … lets `/cancel` compute and return a real
 * fee"*, and *"we compute and record; we do not move money"*. This screen
 * therefore **never** computes a fee — it shows the folio it was given and the
 * fee the server answers with, and the one decision it offers is the one a
 * policy cannot make: waive it.
 *
 * The number *before* the desk commits is one read away —
 * `GET /v1/stays/{id}/cancellation-quote` runs the same `stayCancellationQuote`
 * the cancel then charges, and `core-api`'s own suite asserts the two are the
 * same number. The confirm step here still does not call it: it names the basis
 * (the nights as they were sold, and what has already been taken), offers both
 * outcomes explicitly, and states that nothing is charged either way, and the
 * computed fee, its tier and its days-out land in front of the desk the instant
 * the cancellation does. Reading the quote into the confirm is a screen change
 * with no server behind it to build, and it is a fair next pass.
 *
 * ## The desk books what the site books (V5)
 *
 * `BookStaySheet` below sells a stay through the same `bookStay` the guest site
 * uses, with the total read from `GET /v1/stays/quote` before the button and
 * never sent — the same rule as the fee, from the other side of the counter.
 */

type Bucket = 'arrivals' | 'departures' | 'in_house';

/**
 * The three lists, as catalog keys rather than as words — translated where they
 * are painted, so the row stays the description of the *screen* it always was.
 */
const BUCKETS = [
  { id: 'arrivals', label: 'frontdesk.arriving', blank: 'frontdesk.arrivingBlank' },
  { id: 'departures', label: 'frontdesk.leaving', blank: 'frontdesk.leavingBlank' },
  { id: 'in_house', label: 'frontdesk.inHouse', blank: 'frontdesk.inHouseBlank' },
] as const satisfies readonly { id: Bucket; label: keyof Messages; blank: keyof Messages }[];

export function FrontDesk(): ReactNode {
  const runtime = useRuntime();
  useNow();
  const now = runtime.now();
  const t = useT();
  const locale = useLocale();

  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const currency = session?.tenant.currency ?? 'USD';

  const [picked, setPicked] = useState<string | null>(null);
  const day = picked ?? isoDayIn(timezone, now);
  const [openStay, setOpenStay] = useState<string | null>(null);
  /** The booking sheet (V5), opened on the day the desk is looking at. */
  const [booking, setBooking] = useState(false);
  const canWrite = session?.can_write === true;

  /** Hidden by `App`; silent here. See `Orders.tsx` for why it is checked twice. */
  const entitled = useHasModule('lodging');

  const stays = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/stays/day',
        query: { date: day },
        schema: StayDay,
        signal,
      }),
    [day],
    { cue: (type) => type.startsWith('booking.'), pollMs: 30_000, enabled: entitled },
  );

  /**
   * The calendars two systems share (V3). Read here rather than left to Tasks,
   * because the desk is the screen holding the key: "Airbnb has not answered
   * since yesterday" is a fact about the room you are about to give away, and
   * §5.5.5's answer to "where should a warning live" is "where the decision is".
   */
  const sync = useStaySync();
  const staleCalendars = calendarsNeedingAttention(sync.data?.calendars ?? []);
  const collisions = sync.data?.collisions ?? [];

  const dayData = stays.data;
  const all: Stay[] =
    dayData === null ? [] : [...dayData.arrivals, ...dayData.departures, ...dayData.in_house];
  const stay = all.find((row) => row.booking_id === openStay) ?? null;

  if (!entitled) {
    return (
      <section className="view">
        <p className="empty">{t('frontdesk.notSwitchedOn')}</p>
      </section>
    );
  }

  return (
    <section className="view view--frontdesk">
      <div className="view__header">
        <div className="date-picker">
          <button type="button" className="button" onClick={() => setPicked(addDays(day, -1))}>
            ‹
          </button>
          <input type="date" value={day} onChange={(e) => setPicked(e.target.value)} />
          <button type="button" className="button" onClick={() => setPicked(addDays(day, 1))}>
            ›
          </button>
        </div>
        <h1>{formatDayLong(day, timezone, locale)}</h1>
        <span className="status-strip__staleness">
          {`${formatFreshness(stays.fetchedAt, now, timezone, locale)}${
            stays.error === null ? '' : t('frontdesk.couldNotReach')
          }`}
        </span>
        {/* `can_write` only — hidden, not disabled, the same rule the tab row
            follows: a night auditor reads the house and books nothing. */}
        {canWrite ? (
          <button type="button" className="button button--primary" onClick={() => setBooking(true)}>
            {t('frontdesk.bookStay')}
          </button>
        ) : null}
      </div>

      {stays.loading && stays.fetchedAt === null ? (
        <p className="empty">{t('frontdesk.reading')}</p>
      ) : null}

      <CalendarStaleness
        calendars={staleCalendars}
        staleAfterHours={sync.data?.stale_after_hours ?? 0}
        nowMs={now}
        timezone={timezone}
        t={t}
        locale={locale}
      />

      {collisions.length === 0 ? null : (
        <article className="panel panel--warn">
          <h2>{t('frontdesk.collisionsHeading', { count: collisions.length })}</h2>
          <CollisionAttention collisions={collisions} timezone={timezone} t={t} locale={locale} />
        </article>
      )}

      {BUCKETS.map((bucket) => {
        const rows = dayData === null ? [] : dayData[bucket.id];
        return (
          <article key={bucket.id} className="panel">
            <h2>
              {t(bucket.label)} <span className="lane__count">{rows.length}</span>
            </h2>
            {rows.length === 0 ? (
              <p className="muted">{t(bucket.blank)}</p>
            ) : (
              <ul className="day-list day-list--compact">
                {rows.map((row) => (
                  <li
                    key={`${bucket.id}-${row.booking_id}`}
                    className={`day-row stay-row stay-row--${row.status}`}
                  >
                    <div className="day-row__time">
                      {/* `room_type` is the property's own name for the room. */}
                      <strong>{row.room_type}</strong>
                      <small>{t('frontdesk.room', { room: row.unit + 1 })}</small>
                    </div>
                    <div className="day-row__who">
                      <strong>{row.guest.display_name}</strong>
                      <small>
                        {t('frontdesk.nightsAndGuests', {
                          nights: row.nights,
                          guests: row.guests,
                        })}
                      </small>
                    </div>
                    <div className="day-row__tail">
                      <span className="badge">
                        {formatMoney(row.value_cents, currency, locale)}
                      </span>
                      <small className="muted">{depositWord(row, currency, t, locale)}</small>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => setOpenStay(row.booking_id)}
                        >
                          {t('common.open')}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </article>
        );
      })}

      {stay === null ? null : (
        <StaySheet
          stay={stay}
          currency={currency}
          t={t}
          locale={locale}
          onClose={() => setOpenStay(null)}
          onChanged={() => stays.reload()}
        />
      )}

      {booking ? (
        <BookStaySheet
          day={day}
          currency={currency}
          t={t}
          locale={locale}
          onClose={() => setBooking(false)}
          onChanged={() => stays.reload()}
        />
      ) : null}
    </section>
  );
}

/**
 * The deposit, in a phrase.
 *
 * `prepaid_cents` is money that arrived on an earlier date (§7.4 rule 1), so
 * "paid" here means the folio is settled and "deposit held" means the balance is
 * still to collect. The screen never subtracts anything it was not given.
 */
function depositWord(stay: Stay, currency: string, t: T, locale: UiLocale): string {
  if (stay.prepaid_cents <= 0) return t('frontdesk.nothingTaken');
  if (stay.prepaid_cents >= stay.value_cents) return t('frontdesk.paidInFull');
  return t('frontdesk.depositHeld', {
    amount: formatMoney(stay.prepaid_cents, currency, locale),
  });
}

/* ------------------------------------------------------------------ */
/* One stay, and the cancellation                                      */
/* ------------------------------------------------------------------ */

function StaySheet({
  stay,
  currency,
  t,
  locale,
  onClose,
  onChanged,
}: {
  stay: Stay;
  currency: string;
  t: T;
  locale: UiLocale;
  onClose: () => void;
  onChanged: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const session = useLive(() => runtime.mirror.session(), null);

  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState<StayCancelledType | null>(null);

  const cancel = useCallback(
    async (waive: boolean): Promise<void> => {
      setSending(true);
      setFailure(null);
      try {
        const answer = await runtime.api.request({
          method: 'POST',
          path: `/stays/${stay.booking_id}/cancel`,
          body: {
            ...(waive ? { waive_fee: true } : {}),
            ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
          },
          idempotencyKey: ulid(),
          schema: StayCancelled,
        });
        setCancelled(answer);
        setConfirming(false);
        onChanged();
      } catch (e) {
        // The server's own sentence about this cancellation, verbatim.
        setFailure(e instanceof Error ? e.message : String(e));
      } finally {
        setSending(false);
      }
    },
    [runtime, stay.booking_id, session, onChanged],
  );

  return (
    <div className="sheet" role="dialog" aria-label={t('frontdesk.dialogLabel')}>
      <div className="sheet__panel">
        <h2>{stay.guest.display_name}</h2>
        {/* `status` is the booking's own word on the wire and is not ours. */}
        <p className="muted">
          {t('frontdesk.staySummary', {
            roomType: stay.room_type,
            room: stay.unit + 1,
            nights: stay.nights,
            guests: stay.guests,
            status: stay.status,
          })}
        </p>

        <dl className="facts">
          <dt>{t('frontdesk.folio')}</dt>
          <dd>{formatMoney(stay.value_cents, currency, locale)}</dd>
          <dt>{t('frontdesk.takenAlready')}</dt>
          <dd>{formatMoney(stay.prepaid_cents, currency, locale)}</dd>
          <dt>{t('frontdesk.stillToCollect')}</dt>
          <dd>
            {formatMoney(Math.max(0, stay.value_cents - stay.prepaid_cents), currency, locale)}
          </dd>
          {stay.cancellation_fee_cents > 0 ? (
            <>
              <dt>{t('frontdesk.feeRecorded')}</dt>
              <dd>{formatMoney(stay.cancellation_fee_cents, currency, locale)}</dd>
            </>
          ) : null}
        </dl>

        {stay.notes === '' ? null : <p className="muted">{stay.notes}</p>}

        {cancelled !== null ? (
          <div className="checkout-total" role="status">
            <span className="checkout-total__sum">
              {formatMoney(cancelled.fee_cents, currency, locale)}
            </span>
            <small className="muted">
              {cancelled.waived
                ? t('frontdesk.waived', {
                    amount: formatMoney(cancelled.policy_fee_cents, currency, locale),
                  })
                : t('frontdesk.policyFee', { tier: cancelled.tier })}{' '}
              {t('frontdesk.daysOutLine', {
                days: cancelled.days_out,
                amount: formatMoney(cancelled.accommodation_cents, currency, locale),
              })}
              {cancelled.policy_ref === null
                ? ''
                : t('frontdesk.policyRef', { ref: cancelled.policy_ref })}
            </small>
            <small className="muted">{t('frontdesk.recordedNotCharged')}</small>
          </div>
        ) : null}

        {failure === null ? null : (
          <p className="error" role="alert">
            {failure}
          </p>
        )}

        {stay.status === 'cancelled' || cancelled !== null ? null : confirming ? (
          <div className="editor editor--inline">
            <p className="note">
              {t('frontdesk.confirmLead')}
              <strong>{t('frontdesk.confirmPolicy')}</strong>
              {t('frontdesk.confirmMid', {
                nights: stay.nights,
                amount: formatMoney(stay.value_cents, currency, locale),
              })}
              <strong>{t('frontdesk.confirmStrong')}</strong>
              {t('frontdesk.confirmTail')}
            </p>
            <div className="sheet__actions">
              <button type="button" className="button" onClick={() => setConfirming(false)}>
                {t('frontdesk.keepBooking')}
              </button>
              <button
                type="button"
                className="button"
                disabled={sending}
                onClick={() => void cancel(true)}
              >
                {t('frontdesk.cancelWaive')}
              </button>
              <button
                type="button"
                className="button button--primary"
                disabled={sending}
                onClick={() => void cancel(false)}
              >
                {sending ? t('frontdesk.cancelling') : t('frontdesk.cancelCharge')}
              </button>
            </div>
          </div>
        ) : (
          <div className="row-actions">
            <button
              type="button"
              className="button button--ghost link-button--danger"
              onClick={() => setConfirming(true)}
            >
              {t('frontdesk.cancelStay')}
            </button>
          </div>
        )}

        <div className="sheet__actions">
          <button type="button" className="button" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Booking a stay from the desk (V5, verticals-plan S6)                 */
/* ------------------------------------------------------------------ */

/**
 * "They'll take the King for the 12th to the 15th."
 *
 * **The number before the button.** The dates and the party go to
 * `GET /v1/stays/quote` the moment they are valid, and what comes back is every
 * room type the desk may sell with how many are free for those nights and what
 * the stay would come to — the server's `quoteFor`, which is the same function
 * `bookStay` then writes into the folio. This sheet shows that number and never
 * sends it: the intent carries a room type, two dates and a party, and the
 * server prices the calendar again (B7). A desk that could type the nightly is a
 * desk that could type the wrong one.
 *
 * **Queued, then awaited.** The front desk is an online screen (there is no
 * mirror row for a stay), but a sale is the one thing a tunnel must not lose —
 * so the booking goes through the outbox like every other write and the sheet
 * awaits the drain. Landed: the day re-reads and the sheet says who is in which
 * room. Refused: the server's own sentence, in place, and the intent is off the
 * queue. Still queued: the radio is gone, the sale is safe, and the task list
 * carries it until it lands.
 *
 * The guest is named the two ways `BookSheet` names one — somebody in the book,
 * or a name and a number at the counter — and a walk-in with no number is
 * refused here with the sentence `resolveGuest` would have used, so the desk
 * finds out now rather than from the task list.
 */
function BookStaySheet({
  day,
  currency,
  t,
  locale,
  onClose,
  onChanged,
}: {
  day: string;
  currency: string;
  t: T;
  locale: UiLocale;
  onClose: () => void;
  onChanged: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const clients = useLive(() => runtime.mirror.clients(), []);

  const [checkIn, setCheckIn] = useState(day);
  const [checkOut, setCheckOut] = useState(addDays(day, 1));
  const [guests, setGuests] = useState(1);
  const [serviceId, setServiceId] = useState('');
  const [clientId, setClientId] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<{ kind: 'booked' | 'queued'; room: string } | null>(null);

  const datesValid = checkIn !== '' && checkOut !== '' && checkOut > checkIn;

  // Re-read whenever the ask changes; nothing is polled and nothing is cued,
  // because the answer is a function of the three fields and of the book at
  // this instant, and a stale quote is corrected by the server at booking time.
  const quote = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/stays/quote',
        query: { check_in: checkIn, check_out: checkOut, party_size: guests },
        schema: StayOffers,
        signal,
      }),
    [checkIn, checkOut, guests],
    { pollMs: 0, enabled: datesValid },
  );

  const rooms = quote.data?.rooms ?? [];
  const room = rooms.find((r) => r.service_id === serviceId);
  const client = clients.find((c) => c.client_id === clientId);

  const submit = async (): Promise<void> => {
    setError(null);
    if (!datesValid) return setError(t('frontdesk.errorDates'));
    if (room === undefined) return setError(t('frontdesk.errorRoom'));

    const namedClient = client !== undefined;
    const walkIn = !namedClient && guestName.trim() !== '';
    if (!namedClient && !walkIn) return setError(t('book.errorName'));
    const identities: WireIdentity[] =
      guestPhone.trim() === '' ? [] : [{ identity_kind: 'phone', identity: guestPhone.trim() }];
    if (walkIn && identities.length === 0) return setError(t('book.errorContact'));

    setSending(true);
    try {
      const entry = await actions.bookStay({
        serviceId: room.service_id,
        roomName: room.name,
        checkIn,
        checkOut,
        partySize: guests,
        notes: notes.trim() === '' ? undefined : notes.trim(),
        source: walkIn ? 'walk_in' : 'staff',
        clientName: namedClient ? (client?.display_name ?? '') : guestName.trim(),
        ...(namedClient ? { clientId } : { guest: { name: guestName.trim(), identities } }),
      });
      // The drain the enqueue kicked off, awaited — so "booked" means the
      // server said so, and a refusal is read off the entry it was written to.
      await runtime.outbox.drain();
      const settled = (await runtime.outbox.all()).find((e) => e.id === entry.id);
      if (settled?.status === 'rejected') {
        setError(settled.rejection?.message ?? settled.lastError ?? t('frontdesk.quoteFailed'));
        return;
      }
      setOutcome({ kind: settled?.status === 'done' ? 'booked' : 'queued', room: room.name });
      onChanged();
    } finally {
      setSending(false);
    }
  };

  const clientName = client?.display_name ?? guestName.trim();

  return (
    <div className="sheet" role="dialog" aria-label={t('frontdesk.bookDialogLabel')}>
      <div className="sheet__panel">
        <h2>{t('frontdesk.bookStay')}</h2>

        {outcome !== null ? (
          <p className="note" role="status">
            {outcome.kind === 'booked'
              ? t('frontdesk.booked', { name: clientName, room: outcome.room })
              : t('frontdesk.queued')}
          </p>
        ) : (
          <>
            <label className="field">
              <span>{t('frontdesk.checkIn')}</span>
              <input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
            </label>
            <label className="field">
              <span>{t('frontdesk.checkOut')}</span>
              <input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
            </label>
            <label className="field">
              <span>{t('frontdesk.guestsField')}</span>
              <input
                type="number"
                min={1}
                max={50}
                value={guests}
                onChange={(e) => setGuests(Math.max(1, Number(e.target.value) || 1))}
              />
            </label>

            <label className="field">
              <span>{t('frontdesk.roomField')}</span>
              <select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
                <option value="">{t('frontdesk.pickARoom')}</option>
                {rooms.map((r) => (
                  // A full room type stays on the list, disabled: the desk
                  // should see that the King exists and is gone, not wonder
                  // whether the hotel has one.
                  <option
                    key={r.service_id}
                    value={r.service_id}
                    disabled={r.rooms_available === 0}
                  >
                    {r.rooms_available === 0 || r.value_cents === null
                      ? t('frontdesk.roomFull', { name: r.name })
                      : t('frontdesk.roomOption', {
                          name: r.name,
                          price: formatMoney(r.value_cents, currency, locale),
                          nights: quote.data?.nights ?? 0,
                        })}
                  </option>
                ))}
              </select>
            </label>

            {quote.loading && quote.data === null ? (
              <p className="muted">{t('frontdesk.quoting')}</p>
            ) : null}
            {quote.error !== null ? <p className="error">{t('frontdesk.quoteFailed')}</p> : null}

            {room !== undefined && room.value_cents !== null && room.quote !== null ? (
              <div className="checkout-total" role="status">
                {/* The server's total, shown. It is never in the intent. */}
                <span className="checkout-total__sum">
                  {formatMoney(room.value_cents, currency, locale)}
                </span>
                <small className="muted">
                  {t('frontdesk.quoteBreakdown', {
                    nightly: formatMoney(room.quote.nightly_cents, currency, locale),
                    fees: formatMoney(room.quote.fees_cents, currency, locale),
                    tax: formatMoney(room.quote.tax_cents, currency, locale),
                  })}
                </small>
                {/* `depositWord`'s answer for a folio with nothing taken — a
                    booking made here mints no deposit (S2 is gated on PayPal). */}
                <small className="muted">{t('frontdesk.nothingTaken')}</small>
                <small className="muted">{t('frontdesk.quoteNote')}</small>
              </div>
            ) : null}

            <label className="field">
              <span>{t('book.existingGuest')}</span>
              <select
                value={clientId}
                onChange={(e) => {
                  setClientId(e.target.value);
                  if (e.target.value !== '') setGuestName('');
                }}
              >
                <option value="">{t('book.someoneNew')}</option>
                {clients
                  .filter((c) => !c.archived)
                  .map((c) => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.display_name}
                    </option>
                  ))}
              </select>
            </label>
            {clientId === '' ? (
              <>
                <label className="field">
                  <span>{t('common.name')}</span>
                  <input value={guestName} onChange={(e) => setGuestName(e.target.value)} />
                </label>
                <label className="field">
                  <span>{t('clients.phone')}</span>
                  <input
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    inputMode="tel"
                  />
                </label>
              </>
            ) : null}

            <label className="field">
              <span>{t('common.notes')}</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
            </label>

            {error === null ? null : (
              <p className="error" role="alert">
                {error}
              </p>
            )}
          </>
        )}

        <div className="sheet__actions">
          <button type="button" className="button" onClick={onClose}>
            {t('common.close')}
          </button>
          {outcome === null ? (
            <button
              type="button"
              className="button button--primary"
              disabled={sending || room === undefined}
              onClick={() => void submit()}
            >
              {sending ? t('frontdesk.booking') : t('frontdesk.book')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The calendars two systems share (V3)                                */
/* ------------------------------------------------------------------ */

/**
 * The sync read, **once**, for both screens — this one and Tasks.
 *
 * `GET /v1/stays/sync` answers with every external calendar and every
 * collision that is true right now, which is one round trip for what the desk
 * paints as one warning. Two minutes between polls rather than the thirty
 * seconds the day list uses: the pull behind it runs every fifteen, so a
 * faster poll would be asking a question whose answer cannot have changed.
 *
 * "Once" was the intent from the start and was not what happened: `useRemote`
 * is a subscription per hook call, so with the desk and the task list both
 * open the same route was polled twice on two clocks (plan, seventh pass
 * L11). `useSharedRemote` keys the read by name on the runtime — one timer,
 * one request, both screens reading the one answer — and the shape a screen
 * gets back is `Remote`'s, so neither screen changed.
 */
export function useStaySync(): Remote<StaySyncType> {
  const runtime = useRuntime();
  const entitled = useHasModule('lodging');
  return useSharedRemote(
    'stays/sync',
    (signal) =>
      runtime.api.request({ method: 'GET', path: '/stays/sync', schema: StaySync, signal }),
    { pollMs: 120_000, enabled: entitled },
  );
}

/**
 * Which calendars the desk is told about — **and never silently**.
 *
 * `stale` is the server's word, decided once from its own configured hours so
 * that this screen, the operator's CLI and any future alert cannot disagree
 * about it. A calendar that is switched off, or that names no secret, is not
 * here: nobody asked us to pull it, and a warning about it would teach the
 * desk to stop reading warnings. A calendar whose *last attempt* failed is
 * here even when it is not yet stale, because "Airbnb refused us an hour ago"
 * is worth a line before it becomes worth an alarm.
 */
export function calendarsNeedingAttention(calendars: readonly CalendarSync[]): CalendarSync[] {
  return calendars.filter((c) => c.active && c.configured && (c.stale || c.last_error !== null));
}

/**
 * The staleness line. One sentence per calendar: which listing, how long since
 * we last heard from it, and — when there is one — the reason the last try
 * failed, in the words the sweep recorded.
 *
 * It never names a URL, because the reason string never carries one: an OTA's
 * iCal address is a bearer credential for that room's whole booking calendar.
 */
export function CalendarStaleness({
  calendars,
  staleAfterHours,
  nowMs,
  timezone,
  t,
  locale,
}: {
  calendars: readonly CalendarSync[];
  staleAfterHours: number;
  nowMs: number;
  timezone: string;
  t: T;
  locale: UiLocale;
}): ReactNode {
  if (calendars.length === 0) return null;
  return (
    <article className="panel panel--warn">
      <h2>{t('frontdesk.calendarsHeading', { count: calendars.length })}</h2>
      <ul className="day-list day-list--compact">
        {calendars.map((calendar) => (
          <li key={`${calendar.service_id}-${calendar.ref}`} className="day-row">
            <div className="day-row__who">
              <strong>{calendar.label === '' ? calendar.ref : calendar.label}</strong>
              <small>
                {t('frontdesk.calendarRoom', {
                  room_type: calendar.room_type,
                  room: calendar.unit + 1,
                })}
              </small>
            </div>
            <div className="day-row__tail">
              <small className="muted">
                {calendar.last_pulled_at === null
                  ? t('frontdesk.calendarNeverPulled')
                  : formatStaleness(calendar.last_pulled_at, nowMs, timezone, locale)}
              </small>
              {calendar.last_error === null ? null : (
                <small className="muted">
                  {t('frontdesk.calendarLastError', { error: calendar.last_error })}
                </small>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p className="muted">{t('frontdesk.calendarStaleNote', { hours: staleAfterHours })}</p>
    </article>
  );
}

/**
 * **The collision, as an owner's task** (spec §8.3): an OTA block lying over
 * one of our confirmed stays on the same room.
 *
 * There is no button. That is the whole design and not an omission: §8.3
 * surfaces conflicts and never merges them, and every action a screen could
 * offer here — cancel ours, ignore theirs — is a decision about a guest who
 * has a booking on one side or the other, made by whoever happens to be at the
 * desk. So the card names both sides and the house decides; moving either
 * booking is the ordinary cancel, from the stay it belongs to.
 *
 * Shared with Tasks for `ParcelAttention`'s reason: a room sold twice is an
 * owner's task whichever screen they open first.
 */
/**
 * A collision is about **nights**, so it is dated rather than clocked. Both
 * spans are instants on the wire — a stay is `[check-in time, check-out time +
 * turn)` and an OTA's whole-night block was resolved through the same clock —
 * and a card that printed `2:00 PM to 10:00 AM` for a three-night overlap
 * would be technically true and useless to the person holding the key.
 */
function nightOf(iso: string, timezone: string, locale: UiLocale): string {
  return formatDayLong(isoDayIn(timezone, iso), timezone, locale);
}

export function CollisionAttention({
  collisions,
  timezone,
  t,
  locale,
}: {
  collisions: readonly ExternalCollision[];
  timezone: string;
  t: T;
  locale: UiLocale;
}): ReactNode {
  if (collisions.length === 0) return null;
  return (
    <ul className="task-list task-list--collisions">
      {collisions.map((collision) => (
        <li
          key={`${collision.calendar_ref}-${collision.external_uid}-${collision.booking_id}`}
          className="task task--collision"
        >
          <h2>
            {t('frontdesk.collisionTitle', {
              room_type: collision.room_type,
              room: collision.unit + 1,
            })}
          </h2>
          <p className="task__message">
            {t('frontdesk.collisionOurs', {
              name: collision.guest_first_name,
              from: nightOf(collision.check_in, timezone, locale),
              to: nightOf(collision.check_out, timezone, locale),
            })}
          </p>
          <p className="task__message">
            {t('frontdesk.collisionTheirs', {
              calendar: collision.calendar_ref,
              summary:
                collision.external_summary === ''
                  ? collision.external_uid
                  : collision.external_summary,
              from: nightOf(collision.external_start, timezone, locale),
              to: nightOf(collision.external_end, timezone, locale),
            })}
          </p>
          <p className="task__error muted">{t('frontdesk.collisionNothingCancelled')}</p>
        </li>
      ))}
    </ul>
  );
}
